import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Box, Button, Dialog, DialogActions, DialogContent,
    DialogContentText, DialogTitle, Grid, IconButton, Typography, Radio
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { XClientDataGrid, Loadable } from '../../common';
import { useStatePtr } from '@pure-ptr/react';
import { GridActionsCellItem } from '@mui/x-data-grid-pro';
import { createStringDataField, createPlainDateField } from '../../util/format';

const DEFAULT_SORT = [{ id: 'documentName', direction: 'asc' }];

interface Document {
    id: number;
    documentName: string;
    description: string;
    uploadDate: string;
    uploadedBy: string;
    url: string;
}

interface Props {
    open: boolean;
    documents: Document[];
    onClose: () => void;
    onConfirm: (documentId: number, s3Key: string) => void;
    isDownloading: boolean;
}

const fields = [
    createStringDataField('documentName', 'Document Name', { sortable: true }),
    createStringDataField('description',  'Description',   { sortable: true }),
    createStringDataField('uploadedBy',   'Uploaded By',   { sortable: true }),
    createPlainDateField('uploadDate',    'Upload Date',   { sortable: true }),
];

// Isolated row-level Radio to prevent full grid re-render
const SelectRadio = React.memo(({ rowId, selectedId, onSelect }: {
    rowId: number;
    selectedId: number | null;
    onSelect: (id: number) => void;
}) => (
    <Radio
        size="small"
        checked={selectedId === rowId}
        onChange={() => onSelect(rowId)}
        value={rowId}
    />
));

export function ClaimDocumentSelectDialog({ open, documents, onClose, onConfirm, isDownloading }: Props) {
    const [selectedId, setSelectedId] = useState<number | null>(null);
    const selectedIdRef = useRef<number | null>(null);

    // Reset selection when documents change
    useEffect(() => {
        setSelectedId(null);
        selectedIdRef.current = null;
    }, [documents]);

    const handleSelect = useCallback((id: number) => {
        selectedIdRef.current = id;
        setSelectedId(id);
    }, []);

    const sortPtr = useStatePtr(DEFAULT_SORT);

    const handleConfirm = () => {
        if (selectedId != null) {
            const selectedDoc = documents.find(d => d.id === selectedId);
            onConfirm(selectedId, selectedDoc?.url ?? '');
        }
    };

    const renderSelectAction = useCallback((params) => {
        const rowId = params.row?.id;
        return [
            <GridActionsCellItem
                key={rowId}
                icon={
                    <SelectRadio
                        rowId={rowId}
                        selectedId={selectedIdRef.current}
                        onSelect={handleSelect}
                    />
                }
                label="Select"
                onClick={() => handleSelect(rowId)}
            />
        ];
    }, [handleSelect]);

    const hasDocuments = documents.length > 0;

    return (
        <Dialog maxWidth="lg" fullWidth open={open} onClose={onClose}>
            <Loadable loading={isDownloading}>
            <DialogTitle>
                <Grid container alignItems="center">
                    <Grid item xs={10}>
                        <Typography variant="h6">Select Appeal Form</Typography>
                    </Grid>
                    <Grid item xs={2} className="text-right">
                        <IconButton aria-label="close" onClick={onClose} size="large">
                            <CloseIcon />
                        </IconButton>
                    </Grid>
                </Grid>
            </DialogTitle>

            <DialogContent>
                {/* Empty state — same pattern as PatientDetail */}
                {!hasDocuments ? (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                        <Typography variant="body2" color="text.secondary">
                            No appeal forms configured for this payer.
                        </Typography>
                    </div>
                ) : (
                    // ← sx fixes the extra column gap by constraining grid width
                    <Box sx={{ width: '100%', overflow: 'hidden' }}>
                        <XClientDataGrid
                            fields={fields}
                            getRowId={(row) => row.id}
                            data={documents}
                            sortPtr={sortPtr}
                            loading={false}
                            actionColumnName="Select"
                            actions={renderSelectAction}
                            hideFooter
                            sx={{
                                // Prevent phantom column on right
                                '& .MuiDataGrid-columnHeaders': { width: '100%' },
                                '& .MuiDataGrid-virtualScroller': { width: '100%' },
                            }}
                        />
                    </Box>
                )}
            </DialogContent>

            <DialogActions className="p-3">
                <Button onClick={onClose} variant="outlined">
                    Cancel
                </Button>
                {hasDocuments && (
                    <Button
                        onClick={handleConfirm}
                        variant="contained"
                        disabled={selectedId == null || isDownloading}
                    >
                        {isDownloading ? 'Downloading...' : 'Download Form'}
                    </Button>
                )}
            </DialogActions>
            </Loadable>
        </Dialog>
    );
}